import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #colourPreloadAbort = new AbortController();

  /** @type {IntersectionObserver | null} */
  #cardPreloadObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#scheduleColourImagePreload();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#colourPreloadAbort.abort();
    this.#cardPreloadObserver?.disconnect();
    this.#resizeObserver.disconnect();
  }

  /**
   * After load, and then on idle, warm one featured image per other colour.
   * Product galleries use the variant image the picker already swaps to.
   * Collection cards use the slide image hover already shows, and only when that card is visible.
   */
  #scheduleColourImagePreload() {
    const card = this.closest('product-card');
    const onProductGallery = this.dataset.templateProductMatch === 'true' && !card;
    if (!onProductGallery && !(card instanceof HTMLElement)) return;
    if (this.closest('dialog:not([open])')) return;

    const { signal } = this.#colourPreloadAbort;
    const start = () => {
      if (signal.aborted) return;
      onIdle(() => {
        if (signal.aborted) return;
        if (onProductGallery) {
          enqueueColourPrefetch(this.#colourPreloadUrls());
          return;
        }
        this.#watchCardColours(/** @type {HTMLElement} */ (card));
      });
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true, signal });
    }
  }

  /**
   * Featured media for each unselected colour, at the width the gallery image already requested.
   * One URL per colour option value, not one per size.
   * @returns {string[]}
   */
  #colourPreloadUrls() {
    const sources = /** @type {string[]} */ (
      Array.from(this.querySelectorAll('input[data-option-media-src]'), (input) =>
        input.getAttribute('data-option-media-src')
      ).filter(Boolean)
    );
    const featured = this.#featuredGalleryImage();
    const featuredSrc = featured?.currentSrc || '';
    const featuredId = featuredSrc ? imageIdentity(featuredSrc) : '';
    const width = String(galleryRequestWidth(featured));
    /** @type {string[]} */
    const urls = [];
    const seen = new Set();

    for (const source of sources) {
      if (urls.length >= MAX_COLOUR_PREFETCH) break;
      const src = withImageWidth(source, width);
      const id = imageIdentity(src);
      if (!src || id === featuredId || seen.has(id)) continue;
      seen.add(id);
      urls.push(src);
    }

    return urls;
  }

  /**
   * Prefetch colour slides only once this card is in the viewport.
   * @param {HTMLElement} card
   */
  #watchCardColours(card) {
    const { signal } = this.#colourPreloadAbort;
    const run = () => {
      if (signal.aborted) return;
      enqueueColourPrefetch(this.#cardColourPreloadUrls(card));
    };

    if (typeof IntersectionObserver !== 'function') {
      if (isPartiallyInView(card)) run();
      return;
    }

    this.#cardPreloadObserver?.disconnect();
    this.#cardPreloadObserver = new IntersectionObserver((entries) => {
      if (signal.aborted || !entries.some((entry) => entry.isIntersecting)) return;
      this.#cardPreloadObserver?.disconnect();
      this.#cardPreloadObserver = null;
      onIdle(run);
    });
    this.#cardPreloadObserver.observe(card);
  }

  /**
   * The slideshow image each colour swatch already reveals on hover, sized like the card's visible image.
   * @param {HTMLElement} card
   * @returns {string[]}
   */
  #cardColourPreloadUrls(card) {
    const visibleSlide = card.querySelector('slideshow-slide[aria-hidden="false"]');
    const visibleId = visibleSlide?.getAttribute('slide-id') || '';
    const reference = visibleSlide?.querySelector('img.product-media__image');
    const referenceImage = reference instanceof HTMLImageElement ? reference : null;
    const width = galleryRequestWidth(referenceImage);
    const onScreen = referenceImage?.currentSrc ? imageIdentity(referenceImage.currentSrc) : '';
    /** @type {string[]} */
    const urls = [];
    const seen = new Set();

    for (const input of this.querySelectorAll('input[data-option-media-id]')) {
      if (urls.length >= MAX_COLOUR_PREFETCH) break;
      if (!(input instanceof HTMLInputElement) || input.checked) continue;
      const mediaId = input.getAttribute('data-option-media-id') || '';
      if (!mediaId || mediaId === visibleId) continue;
      const image = slidePreviewImage(card, mediaId);
      if (!image) continue;
      const src = srcsetUrlForWidth(image, width);
      const id = src ? imageIdentity(src) : '';
      if (!src || !id || id === onScreen || seen.has(id)) continue;
      seen.add(id);
      urls.push(src);
    }

    return urls;
  }

  /**
   * The visible featured gallery image. Zoom-dialog copies are excluded.
   * @returns {HTMLImageElement | null}
   */
  #featuredGalleryImage() {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return null;

    const candidates = Array.from(section.querySelectorAll('media-gallery img.product-media__image')).filter(
      (img) => img instanceof HTMLImageElement && !img.closest('zoom-dialog')
    );

    const high = candidates.find((img) => img.getAttribute('fetchpriority') === 'high' && img.offsetWidth > 0);
    if (high) return high;

    return candidates.find((img) => img.offsetWidth > 0) ?? candidates[0] ?? null;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    const resolvedVariant = this.#resolveSelectedVariant(selectedOption);
    /** @type {{ id: string, variantId?: string, available?: boolean }} */
    const resource = {
      id: selectedOption.dataset.optionValueId ?? '',
    };
    if (resolvedVariant) {
      resource.variantId = resolvedVariant.id;
      resource.available = resolvedVariant.available;
    }
    this.dispatchEvent(new VariantSelectedEvent(resource));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = resolvedVariant ? resolvedVariant.id || null : selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * The variant for the options currently checked, resolved without waiting for the section fetch.
   * Combined listings and an unreadable map return null, so the form waits for the server.
   * A combination that does not exist returns an empty id.
   * @param {HTMLElement} selectedOption - The option the shopper just chose.
   * @returns {{ id: string, available: boolean } | null}
   */
  #resolveSelectedVariant(selectedOption) {
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    if (newUrl && currentUrl && newUrl !== currentUrl) return null;

    const script = this.querySelector('script[type="application/json"][data-variants]');
    if (script?.textContent?.trim()) {
      try {
        const variants = JSON.parse(script.textContent);
        if (!Array.isArray(variants)) return null;

        const selectedValues = this.#checkedOptionValues();
        const match = variants.find(
          (variant) =>
            variant &&
            Array.isArray(variant.options) &&
            variant.options.length === selectedValues.length &&
            variant.options.every((option, index) => option === selectedValues[index])
        );

        if (!match || match.id == null) return { id: '', available: false };
        return { id: String(match.id), available: match.available === true };
      } catch {
        return null;
      }
    }

    const fieldsets = this.querySelectorAll('fieldset');
    if (fieldsets.length !== 1) return null;

    const checked = this.querySelector('fieldset input:checked, select option[selected]');
    if (!(checked instanceof HTMLElement)) return null;

    const variantId = checked.dataset.variantId;
    if (!variantId) return null;

    return {
      id: variantId,
      available: checked.dataset.optionAvailable !== 'false',
    };
  }

  /**
   * Option value names in fieldset order. These match `variant.options`.
   * @returns {string[]}
   */
  #checkedOptionValues() {
    return Array.from(this.querySelectorAll('fieldset input:checked, select option[selected]'), (option) => {
      if (option instanceof HTMLInputElement || option instanceof HTMLOptionElement) return option.value;
      return '';
    });
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Reset every radio so stale morph state cannot leave multiple current options.
          for (const radio of radios) {
            radio.dataset.currentChecked = 'false';
            radio.dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

/** One featured image per colour. A large variant matrix stays capped. */
const MAX_COLOUR_PREFETCH = 8;

/** Keep a few low-priority transfers in flight so a grid of cards does not burst. */
const MAX_COLOUR_PREFETCH_IN_FLIGHT = 3;

/** Widths `product-media` puts in the gallery and card srcset. */
const GALLERY_WIDTHS = [240, 352, 832, 1200, 1600, 1920, 2560, 3840];

/** @type {string[]} */
const colourPrefetchQueue = [];

/** @type {Set<string>} */
const colourPrefetchSeen = new Set();

/** @type {HTMLImageElement[]} */
const colourPrefetchImages = [];

let colourPrefetchInFlight = 0;

/**
 * Run after the document has loaded, then when the main thread is idle.
 * `requestIdleCallback` is not used before `window` `load`, so it cannot jump ahead of the first gallery image.
 * @param {() => void} fn
 */
function onIdle(fn) {
  const run = () => {
    const idle = window.requestIdleCallback;
    if (typeof idle === 'function') {
      idle(() => fn(), { timeout: 2000 });
    } else {
      setTimeout(fn, 200);
    }
  };

  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
}

/**
 * @param {string[]} urls
 */
function enqueueColourPrefetch(urls) {
  for (const url of urls) {
    if (!url || colourPrefetchSeen.has(url)) continue;
    colourPrefetchSeen.add(url);
    colourPrefetchQueue.push(url);
  }
  pumpColourPrefetch();
}

function pumpColourPrefetch() {
  while (colourPrefetchInFlight < MAX_COLOUR_PREFETCH_IN_FLIGHT && colourPrefetchQueue.length > 0) {
    const url = colourPrefetchQueue.shift();
    if (!url) break;

    colourPrefetchInFlight += 1;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    colourPrefetchImages.push(image);

    const done = () => {
      colourPrefetchInFlight -= 1;
      if (colourPrefetchQueue.length > 0) onIdle(pumpColourPrefetch);
    };

    image.onload = done;
    image.onerror = done;
    image.src = url;
  }
}

/**
 * Width the browser already chose for this image, snapped to the theme srcset.
 * Falls back to the rendered slot size when `currentSrc` is not ready yet.
 * @param {HTMLImageElement | null | undefined} img
 * @returns {number}
 */
function galleryRequestWidth(img) {
  const fromCurrent = img?.currentSrc ? Number(imageWidth(img.currentSrc)) : 0;
  if (fromCurrent > 0) return snapGalleryWidth(fromCurrent);

  if (img instanceof HTMLImageElement) {
    const rendered = img.clientWidth || img.getBoundingClientRect().width;
    if (rendered > 0) return snapGalleryWidth(Math.ceil(rendered * (window.devicePixelRatio || 1)));
  }

  return 1200;
}

/**
 * @param {number} width
 * @returns {number}
 */
function snapGalleryWidth(width) {
  if (GALLERY_WIDTHS.includes(width)) return width;
  for (const candidate of GALLERY_WIDTHS) {
    if (candidate >= width) return candidate;
  }
  return GALLERY_WIDTHS[GALLERY_WIDTHS.length - 1] ?? 1200;
}

/**
 * @param {HTMLElement} card
 * @param {string} mediaId
 * @returns {HTMLImageElement | null}
 */
function slidePreviewImage(card, mediaId) {
  if (!/^\d+$/.test(mediaId)) return null;
  const image = card.querySelector(`slideshow-slide[slide-id="${mediaId}"] img.product-media__image`);
  return image instanceof HTMLImageElement ? image : null;
}

/**
 * The srcset candidate the card will request when that colour slide is shown.
 * @param {HTMLImageElement} img
 * @param {number} width
 * @returns {string}
 */
function srcsetUrlForWidth(img, width) {
  const srcset = img.getAttribute('srcset') || '';
  let nearest = '';
  let nearestWidth = Infinity;

  for (const part of srcset.split(',')) {
    const bits = part.trim().split(/\s+/);
    const url = bits[0];
    const descriptor = bits[1] || '';
    if (!url) continue;
    if (descriptor === `${width}w`) return url;

    const candidateWidth = descriptor.endsWith('w') ? Number(descriptor.slice(0, -1)) : 0;
    if (candidateWidth >= width && candidateWidth < nearestWidth) {
      nearest = url;
      nearestWidth = candidateWidth;
    }
  }

  if (nearest) return nearest;

  const base = img.getAttribute('src');
  return base ? withImageWidth(base, String(width)) : '';
}

/**
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isPartiallyInView(el) {
  const rect = el.getBoundingClientRect();
  return rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
}

/**
 * @param {string} src
 * @returns {string}
 */
function imageIdentity(src) {
  try {
    const url = new URL(src, window.location.href);
    url.searchParams.delete('width');
    return url.pathname;
  } catch {
    return src.replace(/[?&]width=\d+/, '').replace(/_\d+x(?=\.)/, '');
  }
}

/**
 * @param {string} src
 * @returns {string}
 */
function imageWidth(src) {
  try {
    const width = new URL(src, window.location.href).searchParams.get('width');
    if (width) return width;
  } catch {
    // Fall through to the legacy sized-filename form.
  }

  return src.match(/_(\d+)x\./)?.[1] ?? '';
}

/**
 * @param {string} src
 * @param {string} width
 * @returns {string}
 */
function withImageWidth(src, width) {
  try {
    const url = new URL(src, window.location.href);
    url.searchParams.set('width', width);
    return url.href;
  } catch {
    // Fall through to the legacy sized-filename form.
  }

  if (/_\d+x\./.test(src)) return src.replace(/_\d+x\./, `_${width}x.`);
  return src;
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
